import { ImageXIcon } from '@vizit/icons';
import { cva, cx } from '@vizit/theme';
import React from 'react';
import { Skeleton } from '../skeleton';
import { Text, TextSize } from '../text';

interface ImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  /**
   * Whether the image should have the default border
   *
   * @optional
   * @type boolean
   * @default false
   */
  border?: boolean;
  /**
   * Whether the image should be contained within the container or cover it
   *
   * @optional
   * @type boolean
   * @default true
   */
  contain?: boolean;
  /**
   * Whether the image is broken
   *
   * @optional
   * @type boolean
   */
  isBroken?: boolean;
  /**
   * Whether the image is being uploaded
   *
   * @optional
   * @type boolean
   */
  isUploading?: boolean;
  /**
   * The name of the image (used for alt text)
   *
   * @required
   * @type string
   */
  name: string;
  /**
   * The shape of the image (use unset for no shape) requires consumers to set the aspect ratio.
   * For rectangle to have border radius, set contain to false.
   *
   * @optional
   * @type 'unset' | 'rectangle' | 'square'
   * @default 'rectangle'
   */
  shape?: 'unset' | 'rectangle' | 'square';
  /**
   * The image source URL
   *
   * @required
   * @type string
   */
  src: string | null;
  /**
   * Fallback image source URL to use if the main src fails to load
   *
   * @optional
   * @type string
   */
  fallbackSrc?: string | null;
  /**
   * Whether the image is being analyzed
   *
   * @optional
   * @type boolean
   */
  isAnalyzing?: boolean;
  /**
   * Whether the image is a thumbnail
   *
   * @optional
   * @type boolean
   */
  isThumbnail?: boolean;
  /**
   * The class name for the image
   *
   * @optional
   * @type string
   */
  imageClassName?: string;
}

const styles = {
  root: cva({
    base: 'relative flex-shrink-0 bg-white h-full w-full @container',
    variants: {
      contain: {
        true: 'object-contain',
        false: 'object-cover',
      },
      shape: {
        unset: 'pt-0',
        rectangle: 'pt-[56.25%]',
        square: 'pt-[100%]',
      },
    },
    defaultVariants: {
      contain: true,
      shape: 'unset',
    },
  }),

  errorIcon: cva({
    base: 'text-primary',
    variants: {
      isThumbnail: {
        true: 'size-12',
        false: 'size-24',
      },
    },
  }),
  image: cva({
    base: 'absolute inset-0 h-full w-full',
    variants: {
      isAnalyzing: {
        true: 'opacity-50',
        false: '',
      },
      shape: {
        unset: '',
        rectangle: '',
        square: '',
      },
      border: {
        true: 'border border-primary',
        false: '',
      },
    },
    defaultVariants: {
      isAnalyzing: false,
      shape: 'square',
      border: false,
    },
  }),
};

function Image(props: ImageProps) {
  const {
    border = false,
    className,
    contain: containProp = true,
    fallbackSrc,
    isAnalyzing,
    isBroken,
    isThumbnail = false,
    isUploading,
    name,
    shape = 'square',
    src,
    imageClassName,
    ...other
  } = props;

  const [isLoading, setIsLoading] = React.useState(true);
  const [isError, setIsError] = React.useState(false);
  const [isUsingFallback, setIsUsingFallback] = React.useState(src == null || src === '');

  React.useEffect(() => {
    if (src != null && src !== '') {
      setIsUsingFallback(false);
    }
  }, [src]);

  const currentSrc = isUsingFallback ? fallbackSrc : src;
  const showError = isBroken || isError || currentSrc === 'NoSuchKey';

  const handleOnError = React.useCallback(() => {
    if (!isUsingFallback && fallbackSrc && currentSrc !== fallbackSrc) {
      setIsUsingFallback(true);
      setIsLoading(true);
    } else {
      setIsError(true);
      setIsLoading(false);
    }
  }, [isUsingFallback, fallbackSrc, currentSrc]);

  const handleOnLoad = React.useCallback(() => {
    setIsLoading(false);

    if (src) {
      URL.revokeObjectURL(src);
    }
  }, [src]);

  const contain = !containProp && (shape === 'unset' || shape === 'rectangle') ? false : containProp;

  return (
    <div className={cx(styles.root({ contain, shape }), className)} {...other}>
      {showError && (
        <div
          className={cx(
            'absolute inset-0 flex flex-col items-center justify-center gap-8 bg-primary-disabled rounded-[inherit]',
            {
              shape: shape,
            },
          )}
        >
          <ImageXIcon className={cx(styles.errorIcon({ isThumbnail }))} />
          {!isThumbnail && (
            <Text
              className={cx(
                'text-center font-bold',
                // Hide text in small containers (auto thumbnail detection)
                // Container smaller than 200px = thumbnail behavior
                // This automatically hides text in small containers unless overridden
                'hidden @[200px]:block',
              )}
              size={TextSize.Lg}
            >
              Could not load asset
            </Text>
          )}
          {/* Render a hidden img wrapper without a src to avoid browser re-requesting the page for empty src */}
          {/* otherwise causes:
          An empty string ("") was passed to the src attribute.
          This may cause the browser to download the whole page again over the network.
          To fix this, either do not render the element at all or pass null to src instead of an empty string */}
          <img alt="" aria-hidden="true" className="absolute h-full w-full opacity-0" />
        </div>
      )}
      {!showError && (
        <>
          {currentSrc ? (
            <img
              alt={name}
              className={cx(styles.image({ shape, border }), imageClassName)}
              src={currentSrc}
              style={{ objectFit: 'inherit' }}
              onError={handleOnError}
              onLoad={handleOnLoad}
            />
          ) : null}
          {(isLoading || isUploading || isAnalyzing) && (
            <Skeleton className="vzt-skeleton absolute inset-0 h-full rounded-[inherit]" />
          )}
        </>
      )}
    </div>
  );
}

export { Image };
export type { ImageProps };
